import { NavLink, useNavigate } from 'react-router-dom';
import { X, ChevronDownCircle, ChevronUpCircle } from 'lucide-react';
import { useLogout } from "../hooks/useLogout";
import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import './Sidebar.css'
import { useAuthContext } from '../hooks/useAuthContext';

const Sidebar = ({ isOpen, toggle }) => {
  const { logout } = useLogout();
  const { user } = useAuthContext();
  const navigate = useNavigate()
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // New state for badge counts
  const [bagItemsCount, setBagItemsCount] = useState(0);
  const [savedProductsCount, setSavedProductsCount] = useState(0);
  const [newOrdersCount, setNewOrdersCount] = useState(0);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        setIsAdmin(idTokenResult.claims?.admin || false);
      }
    };
    checkAdminStatus();
  }, [user]);

  // Fetch saved products count
  useEffect(() => {
    const fetchSavedProductsCount = async () => {
      if (!user) {
        setSavedProductsCount(0);
        return;
      }

      try {
        const savedProductsCollection = collection(db, 'savedProducts', user.uid, 'items');
        const savedProductsSnapshot = await getDocs(savedProductsCollection);
        setSavedProductsCount(savedProductsSnapshot.docs.length);
      } catch (error) {
        console.error('Error fetching saved products count:', error);
      }
    };

    fetchSavedProductsCount();
  }, [user]);

  // Fetch new orders count
  useEffect(() => {
    const fetchNewOrdersCount = async () => {
      if (!user) {
        setNewOrdersCount(0);
        return;
      }

      try {
        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef,
          where('userId', '==', user.uid),
          where('isNew', '==', true)
        );

        const querySnapshot = await getDocs(q);
        setNewOrdersCount(querySnapshot.docs.length);
      } catch (error) {
        console.error('Error fetching new orders count:', error);
      }
    };

    fetchNewOrdersCount();
  }, [user]);

  // Fetch My Bag Count
  useEffect(() => {
    const fetchBagItemsCount = async () => {
      if (!user) {
        setBagItemsCount(0);
        return;
      }

      try {
        const itemsCollection = collection(db, 'myBag', user.uid, 'items');
        const itemsSnapshot = await getDocs(itemsCollection);
        const itemsCount = itemsSnapshot.docs.reduce((total, doc) => {
          const itemData = doc.data();
          return total + (itemData.quantity || 1);
        }, 0);
        setBagItemsCount(itemsCount);
      } catch (error) {
        console.error('Error fetching bag items count:', error);
      }
    };

    fetchBagItemsCount();
  }, [user]);

  // Categories data
  const categories = [
    { name: "Formal Shoes", path: "/collections/59WKAKnQQeWACLO3wqFE" },
    { name: "Formal Trouser", path: "/collections/6SPAe62irgkl14LL5trf" },
    { name: "Corporate Shirts", path: "/collections/V3HIjtJx6cAs31vofYLb" },
    { name: "Wristwatches", path: "/collections/YsBq10xHPVh3nnh6z97O" },
    { name: "Suits", path: "/collections/jfJ8K0XfDkeWK1yQ3P8n" },
    { name: "Sneakers", path: "/collections/oYdSojNc8PHgRp0d5Fzi" }
  ];

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }

    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [isOpen]);

  return (
    <>
      <div className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-content">
          <div className="sidebar-header">
            <h1 className="sidebar-brand">XAN OMEGA</h1>
            <button 
              onClick={toggle}
              className="sidebar-close"
            >
              <X size={24} />
            </button>
          </div>

          <nav className="sidebar-nav">
            <div className="sidebar-links">
              <NavLink 
                to="/" 
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                onClick={toggle}
              >
                Home
              </NavLink>

              {user && (
                <NavLink 
                  to="/myaccount" 
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''}`
                  }
                  onClick={toggle}
                >
                  My Account
                </NavLink>
              )}
              <NavLink 
                to="/mybag" 
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                onClick={toggle}
              >
                My Bag 
                {user && bagItemsCount > 0 && (
                  <span className="sidebar-badge">{bagItemsCount}</span>
                )}
              </NavLink>
              <NavLink 
                to="/myorders" 
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                onClick={toggle}
              >
                My Orders 
                {user && newOrdersCount > 0 && (
                  <span className="sidebar-badge">{newOrdersCount}</span>
                )}
              </NavLink>
              <NavLink 
                to="/saved-items" 
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                onClick={toggle}
              >
                Saved Items 
                {user && savedProductsCount > 0 && (
                  <span className="sidebar-badge">{savedProductsCount}</span>
                )}
              </NavLink>

              {/* Categories Collapsible */}
              <div className="sidebar-categories">
                <button 
                  className="categories-toggle"
                  onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
                >
                  Collections
                  {isCategoriesOpen ? <ChevronUpCircle size={20} /> : <ChevronDownCircle size={20} />}
                </button>
                <div className={`categories-content ${isCategoriesOpen ? 'open' : ''}`}>
                  {categories.map((category, index) => (
                    <NavLink 
                      key={index}
                      to={category.path}
                      className={({ isActive }) =>
                        `category-link ${isActive ? 'active' : ''}`
                      }
                      onClick={toggle}
                    >
                      {category.name}
                    </NavLink>
                  ))}
                </div>
              </div>
              <NavLink 
                to="/about" 
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                onClick={toggle}
              >
                About Us
              </NavLink>
              {isAdmin && (
                <NavLink 
                  to="/adminmanagement" 
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''}`
                  }
                  onClick={toggle}
                >
                  Admin Management
                </NavLink>
              )}

              {user ? (
                // User is logged in
                <>
                  <div className="sidebar-footer">
                    <button
                      onClick={() => {
                        logout();
                        toggle();
                        navigate('/');
                      }}
                      className="sidebar-logout-button"
                    >
                      Log Out
                    </button>
                  </div>
                </>
              ) : (
                // User is not logged in
                <>
                  <NavLink 
                    to="/login" 
                    className={({ isActive }) =>
                      `sidebar-link ${isActive ? 'active' : ''}`
                    }
                    onClick={toggle}
                  >
                    Login
                  </NavLink>
                  <NavLink 
                    to="/signup" 
                    className={({ isActive }) =>
                      `sidebar-link ${isActive ? 'active' : ''}`
                    }
                    onClick={toggle}
                  >
                    Sign Up
                  </NavLink>
                </>
              )}
            </div>
          </nav>
        </div>
      </div>
      
      <div 
        className={`sidebar-overlay ${isOpen ? 'active' : ''}`}
        onClick={toggle}
      />
    </>
  );
};

export default Sidebar;